import { useState, useRef, useEffect, useMemo } from 'react';
import FieldToken from './FieldToken.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';

/**
 * Column definitions for the templates table.
 */
export const COLUMN_DEFS = [
  { key: 'output_type_name', label: 'Output Type', sortable: true, filterable: true },
  { key: 'text_format_display', label: 'Template Format', sortable: false, filterable: false },
  { key: 'field_count', label: 'Preview', sortable: true, filterable: false },
];

/** Map output_type_id → display label */
export const OUTPUT_TYPE_MAP = {
  0: 'Global Default',
  3: 'Mobile',
  4: 'Text',
  5: 'Voice',
  6: 'Email',
  7: 'Pager',
};

/** Map output_type_id → badge modifier */
const OUTPUT_BADGE_MOD = {
  0: 'global',
  3: 'mobile',
  4: 'text',
  5: 'voice',
  6: 'email',
  7: 'pager',
};

/** Known field tokens (non-custom) */
const KNOWN_FIELDS = {
  device_name: 'Device Name',
  device_description: 'Device Description',
  manufacture_uuid: 'Serial Number',
  resident_first_name: 'First Name',
  resident_last_name: 'Last Name',
  resident_name: 'Resident Name',
  area_name: 'Area Name',
  near_area_name: 'Near Area',
  event_category: 'Event Category',
  event_type: 'Event Type',
  stage: 'Event Stage',
};

/** Example event data for preview text generation */
const EXAMPLE_EVENT = {
  device_name: 'AIO Station',
  device_description: 'Bathroom',
  manufacture_uuid: '4555684',
  resident_first_name: 'Daniel',
  resident_last_name: 'Avis',
  resident_name: 'Daniel Avis',
  area_name: 'Mountain Spring 301',
  near_area_name: 'West Cafe',
  event_category: 'Emergency',
  event_type: 'Pull Cord Pulled',
  stage: 'CREATE',
};

/** Build a preview string from a text_format CSV */
const buildPreviewText = (textFormat) => {
  if (!textFormat) return '';
  const parts = textFormat.split(',').filter(Boolean);
  return parts.map((p) => {
    if (p.startsWith('custom.')) return p.replace('custom.', '');
    return EXAMPLE_EVENT[p] || p;
  }).join(' ').trim();
};

/**
 * TemplatesTable — data table for existing alert text templates.
 */
const TemplatesTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEdit,
  onDelete,
  onAdd,
  onClearFilters,
  canManage,
}) => {
  // Derive active column definitions
  const activeColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => visibleColumns.includes(c.key)),
    [visibleColumns]
  );

  // Select-all state
  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.template_id));
  const someSelected = data.some((r) => selectedIds.includes(r.template_id)) && !allSelected;

  // Sort icon
  const renderSortIcon = (key) => {
    if (sortColumn !== key) {
      return <i className="fe fe-chevrons-up-down ct-sort-icon ct-sort-icon--neutral" aria-hidden="true" />;
    }
    return sortOrder === 'asc'
      ? <i className="fe fe-chevron-up ct-sort-icon" aria-hidden="true" />
      : <i className="fe fe-chevron-down ct-sort-icon" aria-hidden="true" />;
  };

  // Filter values derived from all data
  const getFilterValues = (key) => {
    if (!allData) return [];
    const vals = [...new Set(allData.map((r) => r[key]).filter(Boolean))];
    return vals.sort((a, b) => String(a).localeCompare(String(b)));
  };

  // Render a single cell
  const renderCell = (row, key) => {
    switch (key) {
      case 'output_type_name': {
        const mod = OUTPUT_BADGE_MOD[row.output_type_id] || 'global';
        return <span className={`ct-output-badge ct-output-badge--${mod}`}>{row.output_type_name}</span>;
      }
      case 'text_format_display': {
        const tokens = (row.text_format || '').split(',').filter(Boolean);
        return (
          <div className="ct-format-cell">
            {tokens.map((t, i) => {
              const isCustom = t.startsWith('custom.');
              const label = isCustom ? null : (KNOWN_FIELDS[t] || t);
              const customVal = isCustom ? t.replace('custom.', '') : null;
              return (
                <FieldToken
                  key={i}
                  label={label}
                  isCustom={isCustom}
                  customValue={customVal}
                  preview
                />
              );
            })}
          </div>
        );
      }
      case 'field_count':
        return <PreviewBadge row={row} />;
      default:
        return <span>{row[key] ?? ''}</span>;
    }
  };

  // Empty state
  if (!data || data.length === 0) {
    const hasFilters = Object.values(filters || {}).some((v) => v?.length > 0);
    return (
      <div className="ct-empty">
        <div className="ct-empty__icon-wrap">
          {hasFilters ? (
            <svg className="ct-icon-xl ct-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
          ) : (
            <svg className="ct-icon-xl ct-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          )}
        </div>
        <h3 className="ct-empty__title">{hasFilters ? 'No results found' : 'No templates yet'}</h3>
        <p className="ct-empty__text">
          {hasFilters
            ? 'No templates match your current search or filter criteria. Try adjusting your search terms or clearing filters.'
            : 'Get started by creating a template to customize alert notification formatting.'}
        </p>
        {hasFilters ? (
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClearFilters}>
            <svg className="ct-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear All Filters
          </button>
        ) : canManage ? (
          <button type="button" className="dp-btn dp-btn--primary" onClick={onAdd}>
            <svg className="ct-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Template
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="ct-table-wrap">
      <table className="ct-table">
        <thead className="ct-thead">
          <tr>
            {/* Checkbox */}
            {canManage ? (
              <th scope="col" className="ct-th ct-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={onSelectAll}
                  className="ct-checkbox"
                  aria-label="Select all templates"
                />
              </th>
            ) : null}

            {/* Actions */}
            {canManage ? (
              <th scope="col" className="ct-th ct-th--actions">
                <span className="ct-sr-only">Actions</span>
              </th>
            ) : null}

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="ct-th">
                <div className="ct-th__content">
                  {col.sortable ? (
                    <button type="button" onClick={() => onSort(col.key)} className="ct-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}
                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="ct-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.template_id);
            return (
              <tr key={row.template_id} className={`ct-tr${isSelected ? ' is-selected' : ''}`}>
                {/* Checkbox */}
                {canManage ? (
                  <td className="ct-td ct-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.template_id)}
                      className="ct-checkbox"
                      aria-label={`Select ${row.output_type_name}`}
                    />
                  </td>
                ) : null}

                {/* Actions */}
                {canManage ? (
                  <td className="ct-td ct-td--actions">
                    <RowActionsMenu row={row} onEdit={onEdit} onDelete={onDelete} />
                  </td>
                ) : null}

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className={`ct-td${col.key === 'text_format_display' ? ' ct-td--format' : ''}`}>
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Preview Badge + Popover
// ---------------------------------------------------------------------------
const PreviewBadge = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const ref = useRef(null);

  const fieldCount = row.field_count || 0;
  const previewText = useMemo(() => buildPreviewText(row.text_format), [row.text_format]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const computePosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverWidth = 320;
    const spaceBelow = window.innerHeight - rect.bottom;

    // Center horizontally, clamp to viewport
    let left = rect.left + rect.width / 2 - popoverWidth / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverWidth - 8));

    if (spaceBelow < 160) {
      setPopoverStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 6}px`, left: `${left}px`, top: 'auto', right: 'auto', width: `${popoverWidth}px`, margin: 0, zIndex: 9999, transform: 'none' });
    } else {
      setPopoverStyle({ position: 'fixed', top: `${rect.bottom + 6}px`, left: `${left}px`, bottom: 'auto', right: 'auto', width: `${popoverWidth}px`, margin: 0, zIndex: 9999, transform: 'none' });
    }
  };

  const handleOpen = (nextOpen) => {
    if (nextOpen) computePosition();
    setOpen(nextOpen);
  };

  if (fieldCount === 0) {
    return <span className="ct-preview-badge ct-preview-badge--empty">0</span>;
  }

  return (
    <div className="ct-preview-cell" ref={ref}>
      <button
        type="button"
        className="ct-preview-badge"
        onClick={() => handleOpen(!open)}
        onMouseEnter={() => handleOpen(true)}
        onMouseLeave={() => handleOpen(false)}
        aria-label={`${fieldCount} field${fieldCount !== 1 ? 's' : ''} — show preview`}
      >
        <svg className="ct-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {fieldCount} field{fieldCount !== 1 ? 's' : ''}
      </button>

      {open && (
        <div className="ct-preview-popover" style={popoverStyle} role="tooltip">
          <p className="ct-preview-popover__title">Alert Text Preview</p>
          <div className="ct-preview-popover__body">
            <p className="ct-preview-popover__text">{previewText || 'No preview available'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row Actions Kebab
// ---------------------------------------------------------------------------
const RowActionsMenu = ({ row, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    if (!open && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuStyle(spaceBelow < 160 ? { bottom: '100%', top: 'auto', marginBottom: '4px' } : { top: '100%', marginTop: '4px' });
    }
    setOpen(!open);
  };

  return (
    <div className="ct-row-actions" ref={menuRef}>
      <button type="button" onClick={handleToggle} className="ct-row-actions__trigger" aria-label="Actions" aria-expanded={open}>
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>
      {open ? (
        <div className="ct-row-actions__menu" style={menuStyle}>
          <button type="button" onClick={() => { onEdit(row); setOpen(false); }} className="ct-row-actions__item">
            <svg className="ct-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit Template
          </button>
          <button type="button" onClick={() => { onDelete(row); setOpen(false); }} className="ct-row-actions__item ct-row-actions__item--danger">
            <svg className="ct-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            Delete Template
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TemplatesTable;
